import { FormEvent, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  evaluatePricing,
  fetchAircraftClassification,
  fetchArchitecture,
  fetchPricingDataset,
  fetchPricingManifest,
  fetchPricingMasterCurrentVersion,
  fetchPricingSpec,
  fetchPricingSummary,
} from "./api/pricing";
import type {
  AircraftClassificationResult,
  PricingAddon,
  PricingAiCreditTier,
  PricingAiTransactionRate,
  PricingAircraftBand,
  PricingCalculationRule,
  PricingCapability,
  PricingDependencyRule,
  PricingEntitlement,
  PricingEvaluateRequest,
  PricingEvaluateResult,
  PricingMasterManifest,
  PricingOperatorProfile,
  PricingProduct,
  PricingQuoteQualifier,
  PricingReferenceData,
  PricingSku,
  PricingSummary,
  PricingUnitOfMeasure,
  PricingVersionCurrent,
} from "./contracts/pricing";
import "./App.css";

type NavKey =
  | "overview"
  | "architecture"
  | "products"
  | "aircraft"
  | "ai-consumption"
  | "price-books"
  | "reference-data"
  | "versions"
  | "audit";

const navItems: Array<{ key: NavKey; label: string }> = [
  { key: "overview", label: "Overview" },
  { key: "architecture", label: "Architecture" },
  { key: "products", label: "Products" },
  { key: "aircraft", label: "Aircraft" },
  { key: "ai-consumption", label: "AI Consumption" },
  { key: "price-books", label: "Price Books" },
  { key: "reference-data", label: "Reference Data" },
  { key: "versions", label: "Versions" },
  { key: "audit", label: "Audit" },
];

function renderNavIcon(key: NavKey): ReactNode {
  const commonProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  switch (key) {
    case "overview":
      return (
        <svg {...commonProps}>
          <path d="M3.8 10.5 12 4l8.2 6.5" />
          <path d="M5.5 9.5V18h13V9.5" />
          <path d="M10 18v-5h4v5" />
        </svg>
      );
    case "architecture":
      return (
        <svg {...commonProps}>
          <path d="M5 18V7.5M19 18V9.5M9.5 18V12h5v6" />
          <path d="M3.5 7.5h17" />
          <path d="M8 4h8l2 3.5H6l2-3.5Z" />
        </svg>
      );
    case "products":
      return (
        <svg {...commonProps}>
          <path d="M4.5 8.5 12 4l7.5 4.5L12 13 4.5 8.5Z" />
          <path d="M4.5 8.5V15l7.5 4.5 7.5-4.5V8.5" />
          <path d="M12 13v7" />
        </svg>
      );
    case "aircraft":
      return (
        <svg {...commonProps}>
          <path d="M3 14.5h18" />
          <path d="M7.5 14.5 10 9.5h4l2.5 5" />
          <path d="M5 17.5h14" />
          <path d="M12 4.5v5" />
        </svg>
      );
    case "ai-consumption":
      return (
        <svg {...commonProps}>
          <path d="M12 3.5V8" />
          <path d="M9 6.5h6" />
          <path d="M6.5 12c0-3.2 2.3-5.5 5.5-5.5s5.5 2.3 5.5 5.5-2.3 5.5-5.5 5.5-5.5-2.3-5.5-5.5Z" />
          <path d="M8.5 15.5 12 12l3.5 3.5" />
        </svg>
      );
    case "price-books":
      return (
        <svg {...commonProps}>
          <path d="M6 4.5h12a1.5 1.5 0 0 1 1.5 1.5v12.5a1.5 1.5 0 0 1-1.5 1.5H6A1.5 1.5 0 0 1 4.5 18.5V6A1.5 1.5 0 0 1 6 4.5Z" />
          <path d="M8.5 8.5h7" />
          <path d="M8.5 12h7" />
          <path d="M8.5 15.5h5" />
        </svg>
      );
    case "reference-data":
      return (
        <svg {...commonProps}>
          <path d="M6 5.5h12a1.5 1.5 0 0 1 1.5 1.5v10.5a1.5 1.5 0 0 1-1.5 1.5H6A1.5 1.5 0 0 1 4.5 17.5V7A1.5 1.5 0 0 1 6 5.5Z" />
          <path d="M8 9.5h8" />
          <path d="M8 12h8" />
          <path d="M8 14.5h5" />
        </svg>
      );
    case "versions":
      return (
        <svg {...commonProps}>
          <path d="M7 7.5h10" />
          <path d="M7 12h10" />
          <path d="M7 16.5h6" />
          <path d="M17.5 18.5A2.5 2.5 0 1 0 17.5 13a2.5 2.5 0 0 0 0 5.5Z" />
        </svg>
      );
    case "audit":
      return (
        <svg {...commonProps}>
          <path d="M12 4.5 18.5 7v5.5c0 3.4-2.2 6.5-6.5 8-4.3-1.5-6.5-4.6-6.5-8V7L12 4.5Z" />
          <path d="m9.8 12.3 1.5 1.5 3.2-3.9" />
        </svg>
      );
    default:
      return null;
  }
}

const productOrder = ["INTELLIGENCE_CORE", "RECORDS_CORE", "OPERATOR", "SHOP", "AI_CREDITS"];

type ArchitectureView = "architecture" | "compare";
type DrawerTab = "overview" | "roles" | "pricing" | "addons" | "included-in" | "dependencies";

const architectureCapabilityCatalog = [
  {
    code: "ASK_AI",
    title: "Ask AI",
    summary: "Natural-language guidance across governed maintenance and operations context.",
    layer: "INTELLIGENCE_CORE",
    group: "Intelligence Core",
    roles: ["Knowledge Curator", "Aviation Maintenance Professional"],
    pricing: "Consumes AI credits",
    addOns: ["AI Credits"],
    status: "Included",
  },
  {
    code: "FAAIQ",
    title: "faaIQ",
    summary: "Operational and safety intelligence for fleet and maintenance workflows.",
    layer: "INTELLIGENCE_CORE",
    group: "Intelligence Core",
    roles: ["Maintenance Planner", "Fleet Manager"],
    pricing: "Consumes AI credits",
    addOns: ["AI Credits"],
    status: "Included",
  },
  {
    code: "HUMANIQ",
    title: "humanIQ",
    summary: "Contextual human-review support for operational intelligence and evidence synthesis.",
    layer: "INTELLIGENCE_CORE",
    group: "Intelligence Core",
    roles: ["Maintenance Control", "Records Manager"],
    pricing: "Consumes AI credits",
    addOns: ["AI Credits"],
    status: "Included",
  },
  {
    code: "PARTSIQ",
    title: "partsIQ",
    summary: "Parts and sourcing guidance with governed evidence and procurement context.",
    layer: "INTELLIGENCE_CORE",
    group: "Intelligence Core",
    roles: ["Parts Coordinator", "Planner"],
    pricing: "Consumes AI credits",
    addOns: ["AI Credits"],
    status: "Included",
  },
  {
    code: "KNOWLEDGE_CURATION",
    title: "Knowledge Curation",
    summary: "Maintains organization-specific operational knowledge and reference content.",
    layer: "INTELLIGENCE_CORE",
    group: "Intelligence Core",
    roles: ["Knowledge Curator"],
    pricing: "Included with licensed aviOS products",
    addOns: [],
    status: "Included",
  },
  {
    code: "RECORDSIQ",
    title: "recordsIQ",
    summary: "Record retrieval, interpretation, and evidence synthesis for aircraft records.",
    layer: "RECORDS_CORE",
    group: "Records Core",
    roles: ["Records Manager", "Read-only Auditor"],
    pricing: "Per tail, includes year-one AI credits by band",
    addOns: ["Records Audit", "Conformity Review"],
    status: "Included",
  },
  {
    code: "OEMIQ",
    title: "oemIQ",
    summary: "OEM guidance and record-linked maintenance context across aircraft history.",
    layer: "RECORDS_CORE",
    group: "Records Core",
    roles: ["Maintenance Planner", "Compliance Manager"],
    pricing: "Per tail",
    addOns: ["Pre-Buy", "Fleet Campaign Assessment"],
    status: "Included",
  },
  {
    code: "TAIL_RECORDS",
    title: "Permanent Tail Record",
    summary: "Permanent aircraft lifecycle record stewardship and evidence continuity.",
    layer: "RECORDS_CORE",
    group: "Records Core",
    roles: ["Records Manager", "Aircraft-management personnel"],
    pricing: "Per tail",
    addOns: ["Historical Migration"],
    status: "Included",
  },
  {
    code: "EVIDENCE_PACKAGES",
    title: "Evidence Packages",
    summary: "Controlled evidence assemblies for maintenance, compliance, and delivery packages.",
    layer: "RECORDS_CORE",
    group: "Records Core",
    roles: ["Inspector", "Records / Closeout Specialist"],
    pricing: "Per tail",
    addOns: ["Conformity Review"],
    status: "Included",
  },
  {
    code: "COMPLIANCEIQ",
    title: "complianceIQ",
    summary: "Airworthiness compliance analysis and regulatory obligation tracking.",
    layer: "OPERATOR",
    group: "Operator",
    roles: ["Compliance Manager", "Director of Maintenance"],
    pricing: "Included in Operator extension",
    addOns: ["A025 Readiness"],
    status: "Included",
  },
  {
    code: "MAINT_PROGRAM",
    title: "Maintenance Program",
    summary: "Program-level maintenance plan and requirement management.",
    layer: "OPERATOR",
    group: "Operator",
    roles: ["Maintenance Planner", "Maintenance Control"],
    pricing: "Included in Operator extension",
    addOns: ["Program Conversion", "Aircraft Induction"],
    status: "Included",
  },
  {
    code: "DUE_FORECAST",
    title: "Due & Forecast",
    summary: "Upcoming maintenance, compliance, and operational readiness forecasting.",
    layer: "OPERATOR",
    group: "Operator",
    roles: ["Maintenance Control", "Fleet Manager"],
    pricing: "Included in Operator extension",
    addOns: [],
    status: "Included",
  },
  {
    code: "AIRCRAFT_READINESS",
    title: "Aircraft Readiness",
    summary: "Aircraft status view across fleet readiness and continuous airworthiness obligations.",
    layer: "OPERATOR",
    group: "Operator",
    roles: ["Director of Maintenance", "Pilot / Crew Reporter"],
    pricing: "Included in Operator extension",
    addOns: [],
    status: "Included",
  },
  {
    code: "MAINT_CONTROL_HANDOFF",
    title: "Maintenance Control Handoff",
    summary: "Operational transfer of maintenance control and accountability between teams.",
    layer: "OPERATOR",
    group: "Operator",
    roles: ["Maintenance Control", "Records Manager"],
    pricing: "Included in Operator extension",
    addOns: [],
    status: "Included",
  },
  {
    code: "SHOPIQ",
    title: "shopIQ",
    summary: "Shop operations planning, execution, and work coordination for maintenance events.",
    layer: "SHOP",
    group: "Shop",
    roles: ["Production Control", "Lead Technician"],
    pricing: "Included in Shop package",
    addOns: ["Workflow Configuration"],
    status: "Included",
  },
  {
    code: "WORK_PACKAGES",
    title: "Work Packages",
    summary: "Structured work packages and execution-ready maintenance tasks.",
    layer: "SHOP",
    group: "Shop",
    roles: ["Planner", "Technician"],
    pricing: "Included in Shop package",
    addOns: ["Workflow Configuration"],
    status: "Included",
  },
  {
    code: "JOB_CARDS",
    title: "Job Cards",
    summary: "Task execution and documentation for shop work packages and maintenance events.",
    layer: "SHOP",
    group: "Shop",
    roles: ["Technician", "Inspector"],
    pricing: "Included in Shop package",
    addOns: ["Workflow Configuration", "Electronic-Signoff Readiness"],
    status: "Included",
  },
  {
    code: "SHIFT",
    title: "Shift",
    summary: "Shop shift planning and work coordination for operational execution sequences.",
    layer: "SHOP",
    group: "Shop",
    roles: ["Production Control", "Lead Technician"],
    pricing: "Included in Shop package",
    addOns: ["Additional Training", "Workflow Configuration"],
    status: "Included",
  },
  {
    code: "SHIFTIQ",
    title: "shiftIQ",
    summary: "Shift performance, escalation, and work-status awareness for shop execution.",
    layer: "SHOP",
    group: "Shop",
    roles: ["Production Control", "Lead Technician"],
    pricing: "Included in Shop package",
    addOns: ["Additional Training", "Workflow Configuration"],
    status: "Included",
  },
  {
    code: "DOC_CLOSEOUT",
    title: "Documentation Closeout",
    summary: "Maintenance close-out, delivery, and evidence completion for customer handoff.",
    layer: "SHOP",
    group: "Shop",
    roles: ["Records / Closeout Specialist", "Quality Manager"],
    pricing: "Included in Shop package",
    addOns: ["Historical Migration", "Workflow Configuration"],
    status: "Included",
  },
];

function formatCurrency(value: number | null | undefined): string {
  if (typeof value !== "number") return "-";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 3 }).format(value);
}

function formatNumber(value: number | null | undefined): string {
  if (typeof value !== "number") return "-";
  return new Intl.NumberFormat("en-US").format(value);
}

function App() {
  const [nav, setNav] = useState<NavKey>("overview");
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [architectureView, setArchitectureView] = useState<ArchitectureView>("architecture");
  const [selectedCapability, setSelectedCapability] = useState<string | null>("ASK_AI");
  const [drawerTab, setDrawerTab] = useState<DrawerTab>("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [summary, setSummary] = useState<PricingSummary | null>(null);
  const [architectureData, setArchitectureData] = useState<any | null>(null);
  const [manifest, setManifest] = useState<PricingMasterManifest | null>(null);
  const [versionCurrent, setVersionCurrent] = useState<PricingVersionCurrent | null>(null);
  const [products, setProducts] = useState<PricingProduct[]>([]);
  const [skus, setSkus] = useState<PricingSku[]>([]);
  const [capabilities, setCapabilities] = useState<PricingCapability[]>([]);
  const [entitlements, setEntitlements] = useState<PricingEntitlement[]>([]);
  const [dependencyRules, setDependencyRules] = useState<PricingDependencyRule[]>([]);
  const [aircraftBands, setAircraftBands] = useState<PricingAircraftBand[]>([]);
  const [operatorProfiles, setOperatorProfiles] = useState<PricingOperatorProfile[]>([]);
  const [aiTransactions, setAiTransactions] = useState<PricingAiTransactionRate[]>([]);
  const [aiCreditTiers, setAiCreditTiers] = useState<PricingAiCreditTier[]>([]);
  const [addons, setAddons] = useState<PricingAddon[]>([]);
  const [units, setUnits] = useState<PricingUnitOfMeasure[]>([]);
  const [qualifiers, setQualifiers] = useState<PricingQuoteQualifier[]>([]);
  const [rules, setRules] = useState<PricingCalculationRule[]>([]);
  const [referenceData, setReferenceData] = useState<PricingReferenceData | null>(null);
  const [apiEndpointCount, setApiEndpointCount] = useState(0);

  const [evalInput, setEvalInput] = useState<PricingEvaluateRequest>({
    product_codes: ["INTELLIGENCE_CORE", "SHOP"],
    aircraft_bands: ["B2"],
    operator_profile: "PART91_OWNER",
    shop_subscription_type: "SHOP_STANDARD",
    operational_users: 7,
    professional_users: 3,
    locations: 2,
    annual_ai_credits: 250000,
  });
  const [evalPending, setEvalPending] = useState(false);
  const [evalError, setEvalError] = useState<string | null>(null);
  const [evalResult, setEvalResult] = useState<PricingEvaluateResult | null>(null);

  const [classifyBand, setClassifyBand] = useState("B3");
  const [classifyResult, setClassifyResult] = useState<AircraftClassificationResult | null>(null);
  const [selectedBuilderProducts, setSelectedBuilderProducts] = useState<string[]>(["INTELLIGENCE_CORE", "SHOP"]);
  const [selectedBuilderCapabilities, setSelectedBuilderCapabilities] = useState<string[]>(["JOB_CARDS", "ASK_AI"]);

  const entitlementCountBySku = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of entitlements) {
      map.set(item.sku_code, (map.get(item.sku_code) ?? 0) + 1);
    }
    return map;
  }, [entitlements]);

  const orderedProducts = useMemo(() => {
    const score = new Map(productOrder.map((code, index) => [code, index]));
    return [...products].sort((a, b) => (score.get(a.product_code) ?? 99) - (score.get(b.product_code) ?? 99));
  }, [products]);

  const capabilityIndex = useMemo(() => new Map(capabilities.map((capability) => [capability.capability_code, capability])), [capabilities]);

  const architectureCapabilities = useMemo(
    () =>
      architectureCapabilityCatalog.map((capability) => ({
        ...capability,
        name: capabilityIndex.get(capability.code)?.name ?? capability.title,
        active: capabilityIndex.get(capability.code)?.active ?? true,
      })),
    [capabilityIndex],
  );

  const selectedCapabilityMeta =
    architectureCapabilities.find((capability) => capability.code === selectedCapability) ?? architectureCapabilities[0];

  function openCapability(code: string, tab: DrawerTab = "overview") {
    setSelectedCapability(code);
    setDrawerTab(tab);
  }

  function getEditionState(code: string, edition: "INTELLIGENCE_CORE" | "RECORDS_CORE" | "OPERATOR" | "SHOP") {
    const operatorCodes = new Set([
      "COMPLIANCEIQ",
      "MAINT_PROGRAM",
      "DUE_FORECAST",
      "AIRCRAFT_READINESS",
      "MAINT_CONTROL_HANDOFF",
    ]);
    const shopCodes = new Set([
      "SHOPIQ",
      "WORK_PACKAGES",
      "JOB_CARDS",
      "SHIFT",
      "SHIFTIQ",
      "DOC_CLOSEOUT",
    ]);
    const recordsCodes = new Set(["RECORDSIQ", "OEMIQ", "TAIL_RECORDS", "EVIDENCE_PACKAGES"]);
    const intelligenceCodes = new Set(["ASK_AI", "FAAIQ", "HUMANIQ", "PARTSIQ", "KNOWLEDGE_CURATION"]);

    if (edition === "INTELLIGENCE_CORE") {
      return intelligenceCodes.has(code) ? "Included" : "Not included";
    }
    if (edition === "RECORDS_CORE") {
      return recordsCodes.has(code) ? "Included" : "Not included";
    }
    if (edition === "OPERATOR") {
      return operatorCodes.has(code) ? "Included" : "Not included";
    }
    if (edition === "SHOP") {
      return shopCodes.has(code) ? "Included" : "Not included";
    }
    return "Not included";
  }

  useEffect(() => {
    async function loadAll(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const [
          nextSummary,
          nextManifest,
          nextVersionCurrent,
          nextSpec,
          productsEnv,
          skusEnv,
          capabilitiesEnv,
          entitlementsEnv,
          dependencyRulesEnv,
          aircraftBandsEnv,
          operatorProfilesEnv,
          aiTransactionsEnv,
          aiCreditTiersEnv,
          addonsEnv,
          unitsEnv,
          qualifiersEnv,
          rulesEnv,
          referenceDataEnv,
        ] = await Promise.all([
          fetchPricingSummary(),
          fetchPricingManifest(),
          fetchPricingMasterCurrentVersion(),
          fetchPricingSpec(),
          fetchPricingDataset("products"),
          fetchPricingDataset("skus"),
          fetchPricingDataset("capabilities"),
          fetchPricingDataset("entitlements"),
          fetchPricingDataset("dependencyRules"),
          fetchPricingDataset("aircraftBands"),
          fetchPricingDataset("operatorProfiles"),
          fetchPricingDataset("aiTransactions"),
          fetchPricingDataset("aiCreditTiers"),
          fetchPricingDataset("addons"),
          fetchPricingDataset("unitsOfMeasure"),
          fetchPricingDataset("quoteQualifiers"),
          fetchPricingDataset("calculationRules"),
          fetchPricingDataset("referenceData"),
        ]);

        setSummary(nextSummary);
        setManifest(nextManifest);
        setVersionCurrent(nextVersionCurrent);
        setApiEndpointCount(nextSpec.endpoints.length);
        setProducts(productsEnv.items);
        setSkus(skusEnv.items);
        setCapabilities(capabilitiesEnv.items);
        setEntitlements(entitlementsEnv.items);
        setDependencyRules(dependencyRulesEnv.items);
        setAircraftBands(aircraftBandsEnv.items);
        setOperatorProfiles(operatorProfilesEnv.items);
        setAiTransactions(aiTransactionsEnv.items);
        setAiCreditTiers(aiCreditTiersEnv.items);
        setAddons(addonsEnv.items);
        setUnits(unitsEnv.items);
        setQualifiers(qualifiersEnv.items);
        setRules(rulesEnv.items);
        setReferenceData((referenceDataEnv.items[0] as PricingReferenceData | undefined) ?? null);
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : "Unable to load Pricing Master datasets");
      } finally {
        setLoading(false);
      }
    }

    void loadAll();
  }, []);

  useEffect(() => {
    void fetchArchitecture()
      .then((data) => setArchitectureData(data))
      .catch(() => setArchitectureData(null));
  }, []);

  function toggleProduct(productCode: string): void {
    setEvalInput((prev) => {
      const current = new Set((prev.product_codes ?? []).map((code) => code.toUpperCase()));
      if (current.has(productCode)) {
        current.delete(productCode);
      } else {
        current.add(productCode);
      }
      return { ...prev, product_codes: Array.from(current) };
    });
  }

  function updateNumberField(
    key: "operational_users" | "professional_users" | "locations" | "annual_ai_credits",
    value: string,
  ): void {
    setEvalInput((prev) => ({
      ...prev,
      [key]: value.trim() ? Number(value) : 0,
    }));
  }

  async function runEvaluation(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setEvalPending(true);
    setEvalError(null);
    try {
      const result = await evaluatePricing(evalInput);
      setEvalResult(result);
    } catch (runError) {
      setEvalError(runError instanceof Error ? runError.message : "Evaluation failed");
    } finally {
      setEvalPending(false);
    }
  }

  async function runClassify(): Promise<void> {
    try {
      const result = await fetchAircraftClassification(classifyBand);
      setClassifyResult(result);
    } catch (classifyError) {
      setClassifyResult({
        band_code: classifyBand,
        disposition: "ERROR",
        status: "REVIEW_REQUIRED",
        note: classifyError instanceof Error ? classifyError.message : "Unable to classify",
      });
    }
  }

  const productSelected = new Set((evalInput.product_codes ?? []).map((code) => code.toUpperCase()));

  function toggleBuilderSelection(list: string[], value: string): string[] {
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
  }

  function renderOverview() {
    return (
      <section className="panel-grid">
        <article className="panel card-grid">
          <h2>Pricing Master Health</h2>
          <div className="kpi-grid">
            <div className="kpi"><span>Version</span><strong>{summary?.pricing_master_version ?? "-"}</strong></div>
            <div className="kpi"><span>Status</span><strong>{summary?.status ?? "-"}</strong></div>
            <div className="kpi"><span>Products</span><strong>{formatNumber(summary?.products)}</strong></div>
            <div className="kpi"><span>SKUs</span><strong>{formatNumber(summary?.skus)}</strong></div>
            <div className="kpi"><span>Capabilities</span><strong>{formatNumber(summary?.capabilities)}</strong></div>
            <div className="kpi"><span>Rules</span><strong>{formatNumber(summary?.calculation_rules)}</strong></div>
          </div>
        </article>

        <article className="panel">
          <h2>Canonical Decisions</h2>
          <ul className="decision-list">
            {(manifest?.canonical_decisions ?? []).map((decision) => (
              <li key={decision}>{decision}</li>
            ))}
          </ul>
        </article>

        <article className="panel">
          <h2>Pricing Evaluation Workbench</h2>
          <p className="muted">This evaluates pricing objects and rule trace only. It does not create a quote.</p>
          <form className="eval-form" onSubmit={runEvaluation}>
            <div className="field-group">
              <label>Products</label>
              <div className="pill-row">
                {orderedProducts.map((product) => {
                  const checked = productSelected.has(product.product_code.toUpperCase());
                  return (
                    <button
                      key={product.product_code}
                      type="button"
                      className={checked ? "pill active" : "pill"}
                      onClick={() => toggleProduct(product.product_code.toUpperCase())}
                    >
                      {product.product_code}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="field-grid">
              <label>
                Aircraft bands (comma-separated)
                <input
                  value={(evalInput.aircraft_bands ?? []).join(",")}
                  onChange={(event) =>
                    setEvalInput((prev) => ({
                      ...prev,
                      aircraft_bands: event.target.value
                        .split(",")
                        .map((item) => item.trim().toUpperCase())
                        .filter(Boolean),
                    }))
                  }
                />
              </label>
              <label>
                Operator profile
                <select
                  value={evalInput.operator_profile ?? ""}
                  onChange={(event) => setEvalInput((prev) => ({ ...prev, operator_profile: event.target.value }))}
                >
                  <option value="">None</option>
                  {operatorProfiles.map((profile) => (
                    <option key={profile.profile_code} value={profile.profile_code}>
                      {profile.profile_code}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Shop subscription type
                <select
                  value={evalInput.shop_subscription_type ?? "SHOP_STANDARD"}
                  onChange={(event) => setEvalInput((prev) => ({ ...prev, shop_subscription_type: event.target.value }))}
                >
                  <option value="SHOP_INDEPENDENT">SHOP_INDEPENDENT</option>
                  <option value="SHOP_STANDARD">SHOP_STANDARD</option>
                  <option value="SHOP_SPECIALTY">SHOP_SPECIALTY</option>
                  <option value="SHOP_ENTERPRISE">SHOP_ENTERPRISE</option>
                </select>
              </label>
              <label>
                Operational users
                <input
                  type="number"
                  min={0}
                  value={evalInput.operational_users ?? 0}
                  onChange={(event) => updateNumberField("operational_users", event.target.value)}
                />
              </label>
              <label>
                Professional users
                <input
                  type="number"
                  min={0}
                  value={evalInput.professional_users ?? 0}
                  onChange={(event) => updateNumberField("professional_users", event.target.value)}
                />
              </label>
              <label>
                Locations
                <input
                  type="number"
                  min={0}
                  value={evalInput.locations ?? 0}
                  onChange={(event) => updateNumberField("locations", event.target.value)}
                />
              </label>
              <label>
                Annual AI credits
                <input
                  type="number"
                  min={0}
                  value={evalInput.annual_ai_credits ?? 0}
                  onChange={(event) => updateNumberField("annual_ai_credits", event.target.value)}
                />
              </label>
            </div>

            <div className="actions">
              <button type="submit" disabled={evalPending}>{evalPending ? "Evaluating..." : "Run evaluation"}</button>
            </div>
          </form>

          {evalError ? <p className="error">{evalError}</p> : null}

          {evalResult ? (
            <div className="result-block">
              <div className="result-kpis">
                <div><span>Annual estimate</span><strong>{formatCurrency(evalResult.annual_total_estimate)}</strong></div>
                <div><span>Monthly estimate</span><strong>{formatCurrency(evalResult.monthly_total_estimate)}</strong></div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>SKU</th><th>Name</th><th>Qty</th><th>Unit</th><th>Extended</th><th>Period</th><th>Disposition</th>
                  </tr>
                </thead>
                <tbody>
                  {evalResult.lines.map((line) => (
                    <tr key={`${line.sku_code}-${line.name}`}>
                      <td>{line.sku_code}</td>
                      <td>{line.name}</td>
                      <td>{formatNumber(line.quantity)}</td>
                      <td>{formatCurrency(line.unit_price)}</td>
                      <td>{formatCurrency(line.extended_price)}</td>
                      <td>{line.billing_period}</td>
                      <td>{line.disposition}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <h3>Rule trace</h3>
              <ul className="trace-list">
                {evalResult.rule_trace.map((rule) => (
                  <li key={rule}>{rule}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </article>
      </section>
    );
  }

  function renderArchitectureDetailDrawer() {
    if (!selectedCapabilityMeta) {
      return null;
    }

    const tabs: DrawerTab[] = ["overview", "roles", "pricing", "addons", "included-in", "dependencies"];

    const tabContent: Record<DrawerTab, ReactNode> = {
      overview: (
        <div className="drawer-content">
          <div className="drawer-kv">
            <div><span>Name</span><strong>{selectedCapabilityMeta.name}</strong></div>
            <div><span>Layer</span><strong>{selectedCapabilityMeta.group}</strong></div>
            <div><span>Status</span><strong>{selectedCapabilityMeta.status}</strong></div>
            <div><span>Summary</span><strong>{selectedCapabilityMeta.summary}</strong></div>
          </div>
        </div>
      ),
      roles: (
        <div className="drawer-content">
          <ul className="decision-list">
            {(selectedCapabilityMeta.roles.length ? selectedCapabilityMeta.roles : ["Role mapping pending"]).map((role) => (
              <li key={role}>{role}</li>
            ))}
          </ul>
          <p className="muted drawer-note">Access to a capability does not independently confer certification, inspection, release, or operational authority.</p>
        </div>
      ),
      pricing: (
        <div className="drawer-content">
          <p className="drawer-pricing">{selectedCapabilityMeta.pricing}</p>
          <p className="muted">Pricing treatment is sourced from the current Pricing Master view and shown without duplicating editable price tables.</p>
        </div>
      ),
      addons: (
        <div className="drawer-content">
          <ul className="decision-list">
            {(selectedCapabilityMeta.addOns.length ? selectedCapabilityMeta.addOns : ["No direct add-ons mapped"]).map((addon) => (
              <li key={addon}>{addon}</li>
            ))}
          </ul>
        </div>
      ),
      "included-in": (
        <div className="drawer-content">
          <ul className="decision-list">
            <li>{selectedCapabilityMeta.group}</li>
            <li>{selectedCapabilityMeta.status}</li>
            <li>Effective entitlement follows the product architecture and direct dependency rules.</li>
          </ul>
        </div>
      ),
      dependencies: (
        <div className="drawer-content">
          <ul className="decision-list">
            {selectedCapabilityMeta.layer === "OPERATOR" ? <li>Requires Records Core</li> : null}
            {selectedCapabilityMeta.layer === "SHOP" ? <li>Requires Shop</li> : null}
            {selectedCapabilityMeta.layer === "RECORDS_CORE" ? <li>Optional with Records Core</li> : null}
            <li>Requires activation review when the governing pricing disposition requires it.</li>
          </ul>
        </div>
      ),
    };

    return (
      <div className="drawer-shell" aria-live="polite">
        <div className="drawer-backdrop" onClick={() => setSelectedCapability(null)} />
        <aside className="drawer-panel" role="dialog" aria-modal="true" aria-label={`${selectedCapabilityMeta.name} capability details`}>
          <div className="drawer-header">
            <div>
              <p className="eyebrow">Capability details</p>
              <h3>{selectedCapabilityMeta.name}</h3>
            </div>
            <button type="button" className="close-button" onClick={() => setSelectedCapability(null)}>Close</button>
          </div>

          <nav className="drawer-tabs" aria-label="Capability tabs">
            {tabs.map((tab) => (
              <button
                key={tab}
                type="button"
                className={drawerTab === tab ? "tab-button active" : "tab-button"}
                onClick={() => setDrawerTab(tab)}
              >
                {tab.replace("-", " ")}
              </button>
            ))}
          </nav>

          {tabContent[drawerTab]}
        </aside>
      </div>
    );
  }

  function renderArchitecture() {
    const foundationCapabilities = [
      "Tenant and organization",
      "Identity and SSO",
      "Role-based access control",
      "Audit trail and provenance",
    ];

    const intelligenceCapabilities = architectureCapabilities.filter((capability) => capability.layer === "INTELLIGENCE_CORE");
    const recordsCapabilities = architectureCapabilities.filter((capability) => capability.layer === "RECORDS_CORE");
    const operatorCapabilities = architectureCapabilities.filter((capability) => capability.layer === "OPERATOR");
    const shopCapabilities = architectureCapabilities.filter((capability) => capability.layer === "SHOP");

    return (
      <section className="panel-grid architecture-panel-grid">
        <article className="panel architecture-hero">
          <div>
            <p className="eyebrow">aviOS product explorer</p>
            <h2>aviOS Product Architecture</h2>
            <p className="muted">Capabilities, roles, pricing, and extensions</p>
          </div>
          <div className="architecture-controls">
            <div className="view-toggle" role="tablist" aria-label="Architecture view">
              <button type="button" className={architectureView === "architecture" ? "toggle active" : "toggle"} onClick={() => setArchitectureView("architecture")}>Architecture</button>
              <button type="button" className={architectureView === "compare" ? "toggle active" : "toggle"} onClick={() => setArchitectureView("compare")}>Compare Editions</button>
            </div>
            <div className="cta-row">
              <button type="button">Build an estimate</button>
              <button type="button" className="secondary-button">Talk to Ironfleet</button>
            </div>
          </div>
        </article>

        {architectureView === "compare" ? (
          <article className="panel">
            <h3>Compare Editions</h3>
            <div className="compare-table-wrap">
              <table className="compare-table">
                <thead>
                  <tr>
                    <th>Capability</th>
                    <th>Intelligence Core</th>
                    <th>Records Core</th>
                    <th>Operator</th>
                    <th>Shop</th>
                  </tr>
                </thead>
                <tbody>
                  {architectureCapabilities.map((capability) => (
                    <tr key={capability.code}>
                      <td>
                        <button type="button" className="compare-link" onClick={() => openCapability(capability.code)}>
                          {capability.name}
                        </button>
                      </td>
                      <td>{getEditionState(capability.code, "INTELLIGENCE_CORE")}</td>
                      <td>{getEditionState(capability.code, "RECORDS_CORE")}</td>
                      <td>{getEditionState(capability.code, "OPERATOR")}</td>
                      <td>{getEditionState(capability.code, "SHOP")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
        ) : (
          <>
            <article className="panel">
              <h3>Architecture summary</h3>
              <div className="architecture-summary-grid">
                <div className="kpi"><span>Foundation</span><strong>Included</strong></div>
                <div className="kpi"><span>Intelligence Core</span><strong>Credit-based</strong></div>
                <div className="kpi"><span>Records Core</span><strong>Per tail</strong></div>
                <div className="kpi"><span>Operator</span><strong>Requires Records Core</strong></div>
                <div className="kpi"><span>Shop</span><strong>Direct from Core</strong></div>
              </div>
            </article>

            <article className="panel architecture-stack-panel">
              <div className="architecture-layer foundation-layer">
                <div className="layer-header">Shared Platform Foundation</div>
                <div className="capability-chip-row">
                  {foundationCapabilities.map((item) => (
                    <span key={item} className="chip">{item}</span>
                  ))}
                </div>
              </div>

              <div className="architecture-layer intelligence-layer">
                <div className="layer-header">Intelligence Core</div>
                <div className="capability-card-grid">
                  {intelligenceCapabilities.map((capability) => (
                    <button key={capability.code} type="button" className="capability-card" onClick={() => openCapability(capability.code)}>
                      <div className="card-title-row">
                        <span>{capability.name}</span>
                        <span className="card-status">{capability.status}</span>
                      </div>
                      <p>{capability.summary}</p>
                      <div className="card-tags">
                        <span>Roles</span>
                        <span>Pricing</span>
                        <span>Add-ons</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="architecture-layer records-layer">
                <div className="layer-header">Records Core</div>
                <div className="capability-card-grid">
                  {recordsCapabilities.map((capability) => (
                    <button key={capability.code} type="button" className="capability-card" onClick={() => openCapability(capability.code)}>
                      <div className="card-title-row">
                        <span>{capability.name}</span>
                        <span className="card-status">{capability.status}</span>
                      </div>
                      <p>{capability.summary}</p>
                      <div className="card-tags">
                        <span>Roles</span>
                        <span>Pricing</span>
                        <span>Add-ons</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="architecture-branch-row">
                <div className="architecture-layer branch-layer operator-layer">
                  <div className="layer-header">Operator</div>
                  <div className="capability-card-grid compact-grid">
                    {operatorCapabilities.map((capability) => (
                      <button key={capability.code} type="button" className="capability-card" onClick={() => openCapability(capability.code)}>
                        <div className="card-title-row">
                          <span>{capability.name}</span>
                          <span className="card-status">{capability.status}</span>
                        </div>
                        <p>{capability.summary}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="architecture-layer branch-layer shop-layer">
                  <div className="layer-header">Shop</div>
                  <div className="capability-card-grid compact-grid">
                    {shopCapabilities.map((capability) => (
                      <button key={capability.code} type="button" className="capability-card" onClick={() => openCapability(capability.code)}>
                        <div className="card-title-row">
                          <span>{capability.name}</span>
                          <span className="card-status">{capability.status}</span>
                        </div>
                        <p>{capability.summary}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </article>

            <article className="panel">
              <h3>Canonical decision highlights</h3>
              <ul className="decision-list">
                <li>Intelligence Core may exist without a tail.</li>
                <li>Records Core is required for Operator and optional for Shop.</li>
                <li>Shop connects directly to Intelligence Core and does not require Records Core.</li>
                <li>Shift and shiftIQ appear only in the Shop branch.</li>
                <li>Job Cards are included as a Shop capability.</li>
              </ul>
            </article>
          </>
        )}

        <article className="panel builder-panel">
          <h3>Quote-builder handoff</h3>
          <div className="builder-selection-grid">
            <div>
              <p className="muted">Selected products</p>
              <div className="chip-row">
                {orderedProducts.map((product) => (
                  <button
                    type="button"
                    key={product.product_code}
                    className={selectedBuilderProducts.includes(product.product_code) ? "chip active-chip" : "chip"}
                    onClick={() => setSelectedBuilderProducts((curr) => toggleBuilderSelection(curr, product.product_code))}
                  >
                    {product.product_code}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="muted">Selected capabilities</p>
              <div className="chip-row">
                {architectureCapabilities.map((capability) => (
                  <button
                    type="button"
                    key={capability.code}
                    className={selectedBuilderCapabilities.includes(capability.code) ? "chip active-chip" : "chip"}
                    onClick={() => setSelectedBuilderCapabilities((curr) => toggleBuilderSelection(curr, capability.code))}
                  >
                    {capability.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="builder-actions">
            <button type="button">Build an estimate</button>
            <button type="button" className="secondary-button">Talk to Ironfleet</button>
          </div>

          <pre className="handoff-json">{JSON.stringify({
            source: "architecture_explorer",
            pricing_master_version: architectureData?.pricing_master_version ?? manifest?.pricing_master_version ?? "current-published-version",
            selected_product_codes: selectedBuilderProducts,
            selected_capability_codes: selectedBuilderCapabilities,
            selected_addon_skus: ["AI-CREDITS-PAYG"],
            display_period: "ANNUAL",
          }, null, 2)}</pre>
        </article>

        {selectedCapability ? renderArchitectureDetailDrawer() : null}
      </section>
    );
  }

  function renderProducts() {
    return (
      <section className="panel-grid">
        <article className="panel">
          <h2>Products</h2>
          <table>
            <thead>
              <tr>
                <th>Code</th><th>Name</th><th>Family</th><th>Type</th><th>Disposition</th><th>Tail Required</th>
              </tr>
            </thead>
            <tbody>
              {orderedProducts.map((product) => (
                <tr key={product.product_code}>
                  <td>{product.product_code}</td>
                  <td>{product.name}</td>
                  <td>{product.family}</td>
                  <td>{product.product_type}</td>
                  <td>{product.pricing_disposition}</td>
                  <td>{product.requires_tail ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>

        <article className="panel">
          <h2>SKUs and entitlements</h2>
          <table>
            <thead>
              <tr>
                <th>SKU</th><th>Product</th><th>Type</th><th>Target Price</th><th>Period</th><th>Entitlements</th>
              </tr>
            </thead>
            <tbody>
              {skus.map((sku) => (
                <tr key={sku.sku_code}>
                  <td>{sku.sku_code}</td>
                  <td>{sku.product_code}</td>
                  <td>{sku.sku_type}</td>
                  <td>{formatCurrency(typeof sku.target_price === "number" ? sku.target_price : null)}</td>
                  <td>{sku.billing_period}</td>
                  <td>{formatNumber(entitlementCountBySku.get(sku.sku_code) ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      </section>
    );
  }

  function renderAircraft() {
    return (
      <section className="panel-grid">
        <article className="panel">
          <h2>Aircraft bands and rates</h2>
          <table>
            <thead>
              <tr>
                <th>Band</th><th>Name</th><th>Records Core Annual</th><th>Starting At</th><th>Year-1 Credits</th><th>Disposition</th>
              </tr>
            </thead>
            <tbody>
              {aircraftBands.map((band) => (
                <tr key={band.band_code}>
                  <td>{band.band_code}</td>
                  <td>{band.name}</td>
                  <td>{formatCurrency(band.records_core_annual_price)}</td>
                  <td>{formatCurrency(band.records_core_starting_at)}</td>
                  <td>{formatNumber(band.year1_credits)}</td>
                  <td>{band.pricing_disposition}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>

        <article className="panel">
          <h2>Classification check</h2>
          <div className="field-inline">
            <input value={classifyBand} onChange={(event) => setClassifyBand(event.target.value.toUpperCase())} />
            <button type="button" onClick={() => void runClassify()}>Classify band</button>
          </div>
          {classifyResult ? (
            <dl className="kv">
              <div><dt>Band</dt><dd>{classifyResult.band_code}</dd></div>
              <div><dt>Status</dt><dd>{classifyResult.status}</dd></div>
              <div><dt>Disposition</dt><dd>{classifyResult.disposition}</dd></div>
              <div><dt>Annual</dt><dd>{formatCurrency(classifyResult.records_core_annual_price)}</dd></div>
              <div><dt>Starting at</dt><dd>{formatCurrency(classifyResult.records_core_starting_at)}</dd></div>
              {classifyResult.note ? <div><dt>Note</dt><dd>{classifyResult.note}</dd></div> : null}
            </dl>
          ) : null}
        </article>
      </section>
    );
  }

  function renderAiConsumption() {
    return (
      <section className="panel-grid">
        <article className="panel">
          <h2>AI transactions</h2>
          <table>
            <thead>
              <tr><th>Transaction</th><th>UOM</th><th>Credits</th><th>USD @ list</th><th>PayGo</th><th>Status</th></tr>
            </thead>
            <tbody>
              {aiTransactions.map((item) => (
                <tr key={item.transaction_code}>
                  <td>{item.transaction_code}</td>
                  <td>{item.uom}</td>
                  <td>{formatNumber(item.credits_per_unit)}</td>
                  <td>{formatCurrency(item.usd_per_unit_at_list)}</td>
                  <td>{item.payg_eligible ? "Yes" : "No"}</td>
                  <td>{item.status ?? "TARGET"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>

        <article className="panel">
          <h2>AI credit tiers</h2>
          <table>
            <thead>
              <tr><th>Rate code</th><th>Min Credits</th><th>Max Credits</th><th>USD/Credit</th><th>Status</th></tr>
            </thead>
            <tbody>
              {aiCreditTiers.map((tier) => (
                <tr key={tier.rate_code}>
                  <td>{tier.rate_code}</td>
                  <td>{formatNumber(tier.minimum_annual_credits)}</td>
                  <td>{formatNumber(tier.maximum_annual_credits)}</td>
                  <td>{formatCurrency(tier.usd_per_credit)}</td>
                  <td>{tier.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      </section>
    );
  }

  function renderPriceBooks() {
    return (
      <section className="panel-grid">
        <article className="panel">
          <h2>Operator profiles and minimums</h2>
          <table>
            <thead>
              <tr><th>Profile</th><th>Name</th><th>Annual Minimum</th><th>Disposition</th></tr>
            </thead>
            <tbody>
              {operatorProfiles.map((profile) => (
                <tr key={profile.profile_code}>
                  <td>{profile.profile_code}</td>
                  <td>{profile.name}</td>
                  <td>{formatCurrency(profile.annual_platform_minimum)}</td>
                  <td>{profile.pricing_disposition ?? "FIXED"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>

        <article className="panel">
          <h2>Add-ons</h2>
          <table>
            <thead>
              <tr><th>SKU</th><th>Family</th><th>Category</th><th>Low</th><th>High</th><th>Disposition</th></tr>
            </thead>
            <tbody>
              {addons.map((addon) => (
                <tr key={addon.sku_code}>
                  <td>{addon.sku_code}</td>
                  <td>{addon.product_family}</td>
                  <td>{addon.category}</td>
                  <td>{formatCurrency(addon.target_low)}</td>
                  <td>{formatCurrency(addon.target_high)}</td>
                  <td>{addon.pricing_disposition}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>

        <article className="panel">
          <h2>Calculation rules</h2>
          <table>
            <thead>
              <tr><th>Rule</th><th>Type</th><th>Priority</th><th>Notes</th></tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.rule_code}>
                  <td>{rule.rule_code}</td>
                  <td>{rule.rule_type}</td>
                  <td>{formatNumber(rule.priority)}</td>
                  <td>{rule.application ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      </section>
    );
  }

  function renderReferenceData() {
    return (
      <section className="panel-grid">
        <article className="panel">
          <h2>Reference lists</h2>
          {referenceData
            ? Object.entries(referenceData).map(([key, values]) => (
                <div key={key} className="ref-group">
                  <h3>{key}</h3>
                  <div className="chip-row">
                    {values.map((value) => (
                      <span key={value} className="chip">{value}</span>
                    ))}
                  </div>
                </div>
              ))
            : <p className="muted">No reference data loaded.</p>}
        </article>

        <article className="panel">
          <h2>Units of measure</h2>
          <table>
            <thead>
              <tr><th>UOM</th><th>Precision</th><th>Rounding</th><th>Active</th></tr>
            </thead>
            <tbody>
              {units.map((uom) => (
                <tr key={uom.uom_code}>
                  <td>{uom.uom_code}</td>
                  <td>{uom.quantity_precision}</td>
                  <td>{uom.rounding}</td>
                  <td>{uom.active ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>

        <article className="panel">
          <h2>Qualifiers</h2>
          <table>
            <thead>
              <tr><th>Qualifier</th><th>Label</th><th>Type</th><th>Required</th></tr>
            </thead>
            <tbody>
              {qualifiers.map((qualifier) => (
                <tr key={qualifier.qualifier_code}>
                  <td>{qualifier.qualifier_code}</td>
                  <td>{qualifier.label}</td>
                  <td>{qualifier.response_type}</td>
                  <td>{qualifier.required ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      </section>
    );
  }

  function renderVersions() {
    return (
      <section className="panel-grid">
        <article className="panel">
          <h2>Current version</h2>
          <dl className="kv">
            <div><dt>Version</dt><dd>{versionCurrent?.version ?? "-"}</dd></div>
            <div><dt>Status</dt><dd>{versionCurrent?.status ?? "-"}</dd></div>
            <div><dt>Effective</dt><dd>{versionCurrent?.effective_from ?? "-"}</dd></div>
            <div><dt>Currency</dt><dd>{versionCurrent?.currency ?? "-"}</dd></div>
            <div><dt>Package</dt><dd>{versionCurrent?.package_name ?? "-"}</dd></div>
          </dl>
        </article>

        <article className="panel">
          <h2>Dataset manifest</h2>
          <table>
            <thead>
              <tr><th>File</th><th>Record Count</th></tr>
            </thead>
            <tbody>
              {(manifest?.datasets ?? []).map((dataset) => (
                <tr key={dataset.file}>
                  <td>{dataset.file}</td>
                  <td>{formatNumber(dataset.record_count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      </section>
    );
  }

  function renderAudit() {
    return (
      <section className="panel-grid">
        <article className="panel">
          <h2>Audit readiness objects</h2>
          <p className="muted">
            Phase 1 seed package is currently static JSON. This workspace includes validation rules, version metadata,
            dependency rules, and canonical decisions ready to back audit events once write workflows are introduced.
          </p>
          <ul className="trace-list">
            <li>Version metadata object loaded from manifest</li>
            <li>Dataset lineage preserved by source file and record counts</li>
            <li>Rule trace generated by the pricing evaluation endpoint</li>
            <li>Dependency validations surfaced in evaluation response</li>
            <li>API surface endpoints discovered: {apiEndpointCount}</li>
          </ul>
        </article>

        <article className="panel">
          <h2>Dependency matrix</h2>
          <table>
            <thead>
              <tr><th>Rule</th><th>Relationship</th><th>Source</th><th>Target</th><th>Validation</th></tr>
            </thead>
            <tbody>
              {dependencyRules.map((rule) => (
                <tr key={rule.rule_code}>
                  <td>{rule.rule_code}</td>
                  <td>{rule.relationship}</td>
                  <td>{rule.source_product ?? rule.source_capability ?? "-"}</td>
                  <td>{rule.target_product ?? "-"}</td>
                  <td>{rule.validation_code}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>

        <article className="panel">
          <h2>Capability inventory</h2>
          <div className="chip-row">
            {capabilities.map((capability) => (
              <span key={capability.capability_code} className={capability.active ? "chip" : "chip muted-chip"}>
                {capability.capability_code}
              </span>
            ))}
          </div>
        </article>
      </section>
    );
  }

  function renderContent() {
    if (loading) {
      return <p className="state">Loading Pricing Master datasets...</p>;
    }
    if (error) {
      return <p className="state error">{error}</p>;
    }

    if (nav === "overview") return renderOverview();
    if (nav === "architecture") return renderArchitecture();
    if (nav === "products") return renderProducts();
    if (nav === "aircraft") return renderAircraft();
    if (nav === "ai-consumption") return renderAiConsumption();
    if (nav === "price-books") return renderPriceBooks();
    if (nav === "reference-data") return renderReferenceData();
    if (nav === "versions") return renderVersions();
    return renderAudit();
  }

  return (
    <main className={navCollapsed ? "app-shell nav-collapsed" : "app-shell"}>
      <aside className={navCollapsed ? "nav-shell collapsed" : "nav-shell"}>
        <div className="nav-header-row">
          <div>
            <h1>aviOS Pricing Master</h1>
            {!navCollapsed ? <p className="muted">Phase 1 administration shell</p> : null}
          </div>
          <button
            type="button"
            className="nav-toggle"
            aria-label={navCollapsed ? "Expand navigation" : "Collapse navigation"}
            title={navCollapsed ? "Expand navigation" : "Collapse navigation"}
            onClick={() => setNavCollapsed((value) => !value)}
          >
            {navCollapsed ? "»" : "«"}
          </button>
        </div>
        <nav>
          {navItems.map((item) => (
            <button
              type="button"
              key={item.key}
              className={item.key === nav ? "nav-item active" : "nav-item"}
              onClick={() => setNav(item.key)}
              title={item.label}
              aria-label={item.label}
            >
              <span className="nav-icon">{renderNavIcon(item.key)}</span>
              {!navCollapsed ? <span className="nav-label">{item.label}</span> : null}
            </button>
          ))}
        </nav>
      </aside>

      <section className="content-shell">
        <header className="content-header">
          <div>
            <h2>{navItems.find((item) => item.key === nav)?.label}</h2>
            <p className="muted">Scope excludes quote creation flows for this phase.</p>
          </div>
        </header>
        {renderContent()}
      </section>
    </main>
  );
}

export default App;
